(() => {
  if (customElements.get("aube-reviews")) return;

  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const stars = (value) => {
    const node = el("span", "ar-stars", "★".repeat(value) + "☆".repeat(5 - value));
    node.setAttribute("role", "img");
    node.setAttribute("aria-label", `${value} sur 5`);
    return node;
  };

  // Photo and mapping sources may be HTTPS URLs or ordinary relative URLs.
  // Protocol-relative URLs, credentials, control characters and other schemes
  // are deliberately rejected, without inserting any mapping text as HTML.
  const safeSource = (value) => {
    if (typeof value !== "string") return null;
    const source = value.trim();
    if (!source || /[\\\u0000-\u001f\u007f]/.test(source) || source.startsWith("//")) return null;
    const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(source);
    if (hasScheme && !/^https:\/\//i.test(source)) return null;
    try {
      const url = new URL(source, document.baseURI);
      if (!["https:", "http:"].includes(url.protocol) || url.username || url.password) return null;
      return url.href;
    } catch {
      return null;
    }
  };

  class AubeReviews extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.page = 1;
      this.rating = "0";
      this.sequence = 0;
      this.mode = this.dataset.mode === "home" ? "home" : "full";
      this.endpoint = this.dataset.endpoint || "";
      const fixture = this.querySelector("script[data-ar-fixture]");
      this.demo = !!fixture;
      this.sort = this.demo ? "photos" : "recent";
      try {
        this.fixtures = fixture ? JSON.parse(fixture.textContent) : [];
      } catch {
        this.fixtures = [];
      }
      this.photoMap = new Map();
      // Capture inline configuration before render() replaces the light DOM.
      const inlineMap = this.querySelector("script[data-ar-photo-map]");
      this.photoMapReady = this.demo
        ? this.loadPhotoMap(inlineMap ? inlineMap.textContent : null, this.dataset.photoMapUrl)
        : Promise.resolve();
      this.render();
      this.load(false);
    }

    async loadPhotoMap(inlineText, source) {
      try {
        let data;
        if (inlineText !== null) {
          data = JSON.parse(inlineText);
        } else {
          const url = safeSource(source);
          if (!url) return;
          const response = await fetch(url, {
            credentials: "omit",
            signal: AbortSignal.timeout(12000),
          });
          if (!response.ok) return;
          data = await response.json();
        }
        if (!data || !Array.isArray(data.entries)) return;
        const knownIds = new Set(this.fixtures.map((review) => String(review.id)));
        for (const entry of data.entries) {
          if (!entry || entry.reviewId == null || !Array.isArray(entry.photos)) continue;
          const reviewId = String(entry.reviewId);
          if (!knownIds.has(reviewId) || this.photoMap.has(reviewId)) continue;
          const seen = new Set();
          const photos = [];
          for (const photo of entry.photos) {
            if (!photo || typeof photo !== "object") continue;
            const src = safeSource(photo.src);
            if (!src || seen.has(src)) continue;
            seen.add(src);
            photos.push({
              src,
              alt: typeof photo.alt === "string" && photo.alt.trim()
                ? photo.alt.trim()
                : "Photo de la taie",
            });
          }
          if (photos.length) this.photoMap.set(reviewId, photos);
        }
      } catch {
        // An unavailable or invalid map must not hide the existing reviews.
      }
    }

    photosFor(review) {
      return this.demo ? this.photoMap.get(String(review.id)) || [] : [];
    }

    render() {
      this.root = el("section", `ar ar-${this.mode}`);
      this.root.id = "avis";
      this.root.setAttribute("aria-label", this.demo ? "Avis" : "Avis sur La Taie du Soir");
      this.replaceChildren(this.root);
      const head = el("div", "ar-head");
      const intro = el("div");
      intro.append(
        el("span", "ar-kicker", "Aube & vous"),
        el("h2", "", this.mode === "home" ? "Vos nuits, vos avis." : "Vos avis sur La Taie du Soir."),
        el("p", "", this.mode === "home"
          ? "Découvrez les retours partagés sur notre taie en soie."
          : "Votre expérience compte, des premières nuits à l’entretien."),
      );
      head.append(intro);
      this.write = el("button", "ar-button", "Partager mon avis");
      this.write.type = "button";
      this.write.addEventListener("click", () => this.dialog.showModal());
      if (this.mode === "home") {
        const link = el("a", "ar-button secondary", "Voir tous les avis");
        link.href = this.dataset.productUrl || "/products/la-taie-du-soir#avis";
        head.append(link);
      } else {
        head.append(this.write);
      }
      this.root.append(head);
      this.summary = el("div", "ar-summary");
      this.root.append(this.summary);
      if (this.mode === "full") {
        const tools = el("div", "ar-tools");
        this.result = el("span", "ar-note");
        const selects = el("div", "ar-selects");
        const sortOptions = [
          ["recent", "Plus récents"],
          ["highest", "Meilleures notes"],
          ["lowest", "Moins bonnes notes"],
        ];
        if (this.demo) sortOptions.unshift(["photos", "Avec photos d’abord"]);
        for (const [key, label, options] of [
          ["rating", "Note", [
            ["0", "Toutes les notes"], ["5", "5 étoiles"], ["4", "4 étoiles"],
            ["3", "3 étoiles"], ["2", "2 étoiles"], ["1", "1 étoile"],
          ]],
          ["sort", "Trier par", sortOptions],
        ]) {
          const field = el("label", "", label);
          const select = el("select");
          select.name = key;
          for (const [value, text] of options) {
            const option = el("option", "", text);
            option.value = value;
            select.append(option);
          }
          select.value = this[key];
          select.addEventListener("change", () => {
            this[key] = select.value;
            this.page = 1;
            this.load(false);
          });
          field.append(select);
          selects.append(field);
        }
        tools.append(this.result, selects);
        this.root.append(tools);
      }
      this.status = el("p", "ar-status");
      this.status.setAttribute("role", "status");
      this.status.setAttribute("aria-live", "polite");
      this.grid = el("div", "ar-grid");
      this.root.append(this.status, this.grid);
      this.bottom = el("div", "ar-bottom");
      this.more = el("button", "ar-button secondary", "Voir plus d’avis");
      this.more.type = "button";
      this.more.hidden = true;
      this.more.addEventListener("click", () => {
        this.page++;
        this.load(true);
      });
      this.bottom.append(this.more);
      this.root.append(this.bottom);
      if (this.mode === "full") {
        const help = el("details", "ar-help");
        help.append(
          el("summary", "", "Comment les avis sont-ils publiés ?"),
          el("p", "", "Les avis sont relus avant publication. Les retours positifs et négatifs sont acceptés. La modération écarte le spam, les doublons, les propos abusifs, les contenus sans rapport et les informations personnelles. Les avis spontanés ne sont pas présentés comme des achats vérifiés. Pour demander le retrait de votre avis : contact@aubeetelle.fr."),
        );
        this.root.append(help);
        this.buildForm();
      }
      if (this.demo) this.buildPhotoDialog();
    }

    makeSummary(summary) {
      this.reviewSummary = { product: this.dataset.product || "la-taie-du-soir", summary, isDemo: this.demo };
      this.dispatchEvent(new CustomEvent("aube:reviews-summary", { detail: this.reviewSummary, bubbles: true }));
      this.summary.replaceChildren();
      if (!summary.count) {
        this.summary.hidden = true;
        return;
      }
      this.summary.hidden = false;
      const score = el("div", "ar-score");
      score.append(
        el("strong", "", Number(summary.average).toLocaleString("fr-FR", {
          minimumFractionDigits: 1, maximumFractionDigits: 1,
        })),
        el("span", "", " / 5"),
        el("div", "ar-note", `${summary.count} ${this.demo ? "avis" : summary.count === 1 ? "avis publié" : "avis publiés"}`),
      );
      const histogram = el("div", "ar-histogram");
      for (let rating = 5; rating >= 1; rating--) {
        const row = el("div", "ar-bar");
        const meter = el("meter");
        meter.min = 0;
        meter.max = summary.count;
        meter.value = summary.histogram[rating] || 0;
        meter.setAttribute("aria-label", `${rating} ${rating === 1 ? "étoile" : "étoiles"} : ${meter.value} avis`);
        row.append(el("span", "", `${rating} ★`), meter, el("span", "", String(meter.value)));
        histogram.append(row);
      }
      this.summary.append(score, histogram);
      if (!this.demo) this.summary.append(el("p", "ar-summary-note", "Toutes les notes comptent. La moyenne inclut tous les avis publiés sur ce produit."));
    }

    card(review) {
      const card = el("article", "ar-card");
      const top = el("div", "ar-card-top");
      top.append(stars(review.rating));
      if (this.demo) top.append(el("span", "ar-fixture-badge", "Avis"));
      card.append(top, el("h3", "", review.title));
      if (review.body.length > 360) {
        card.append(el("p", "", review.body.slice(0, 330) + "…"));
        const details = el("details");
        details.append(el("summary", "", "Lire l’avis complet"), el("p", "", review.body));
        card.append(details);
      } else {
        card.append(el("p", "", review.body));
      }
      const photos = this.photosFor(review);
      if (photos.length) {
        const gallery = el("div", "ar-photos");
        photos.forEach((photo, index) => {
          const button = el("button", "ar-photo");
          button.type = "button";
          button.setAttribute("aria-label", `Agrandir la photo ${index + 1}`);
          button.setAttribute("aria-haspopup", "dialog");
          const image = el("img");
          image.src = photo.src;
          image.alt = photo.alt;
          image.loading = "lazy";
          image.decoding = "async";
          image.width = 800;
          image.height = 800;
          button.append(image);
          button.addEventListener("click", () => this.openPhoto(photo, button));
          gallery.append(button);
        });
        card.append(gallery);
      }
      const foot = el("footer");
      foot.append(el("strong", "", review.name));
      if (!this.demo) {
        const date = new Date(review.created_at * 1000);
        const time = el("time", "", date.toLocaleDateString("fr-FR"));
        time.dateTime = date.toISOString();
        foot.append(time, el("div", "ar-note", "Avis spontané"));
      }
      card.append(foot);
      return card;
    }

    buildPhotoDialog() {
      this.photoDialog = el("dialog", "ar-photo-dialog");
      this.photoDialog.setAttribute("aria-label", "Photo agrandie");
      const close = el("button", "ar-close ar-photo-close", "×");
      close.type = "button";
      close.setAttribute("aria-label", "Fermer la photo");
      close.addEventListener("click", () => this.photoDialog.close());
      this.photoImage = el("img", "ar-photo-full");
      this.photoDialog.append(close, this.photoImage);
      this.photoDialog.addEventListener("cancel", (event) => {
        event.preventDefault();
        this.photoDialog.close();
      });
      this.photoDialog.addEventListener("close", () => {
        if (this.photoTrigger && this.photoTrigger.isConnected) this.photoTrigger.focus();
        this.photoImage.removeAttribute("src");
        this.photoTrigger = null;
      });
      this.root.append(this.photoDialog);
    }

    openPhoto(photo, trigger) {
      this.photoTrigger = trigger;
      this.photoImage.src = photo.src;
      this.photoImage.alt = photo.alt;
      this.photoDialog.showModal();
    }

    async load(append) {
      const sequence = ++this.sequence;
      this.more.disabled = true;
      this.status.textContent = "Chargement des avis…";
      this.root.setAttribute("aria-busy", "true");
      try {
        let data;
        const size = this.mode === "home" ? 3 : 6;
        if (this.demo) {
          await this.photoMapReady;
          if (sequence !== this.sequence) return;
          const histogram = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
          this.fixtures.forEach((review) => histogram[review.rating]++);
          let list = this.fixtures.filter((review) => this.rating === "0" || review.rating === Number(this.rating));
          // Sort the complete filtered set before pagination. Stable ties retain
          // the fixture's existing recent order; source records are not mutated.
          if (this.sort === "photos") {
            list.sort((a, b) => Number(this.photosFor(b).length > 0) - Number(this.photosFor(a).length > 0));
          } else if (this.sort !== "recent") {
            list.sort((a, b) => this.sort === "highest" ? b.rating - a.rating : a.rating - b.rating);
          }
          data = {
            summary: {
              count: this.fixtures.length,
              average: this.fixtures.reduce((sum, review) => sum + review.rating, 0) / this.fixtures.length,
              histogram,
            },
            reviews: list.slice((this.page - 1) * size, this.page * size),
            total: list.length,
            has_more: this.page * size < list.length,
          };
        } else if (!this.endpoint) {
          data = { summary: { count: 0 }, reviews: [], total: 0, has_more: false };
          this.write.disabled = true;
          this.write.textContent = "Formulaire bientôt disponible";
        } else {
          const url = new URL(this.endpoint);
          url.search = new URLSearchParams({
            product: this.dataset.product || "la-taie-du-soir",
            rating: this.rating, sort: this.sort, page: this.page, size,
          });
          const response = await fetch(url, { credentials: "omit", signal: AbortSignal.timeout(12000) });
          if (!response.ok) throw new Error();
          data = await response.json();
        }
        if (sequence !== this.sequence) return;
        this.makeSummary(data.summary);
        if (!append) this.grid.replaceChildren();
        data.reviews.forEach((review) => this.grid.append(this.card(review)));
        if (!data.reviews.length && !append) {
          const empty = el("div", "ar-empty");
          empty.append(
            el("h3", "", data.summary.count ? "Aucun avis avec cette note." : "Le premier avis peut être le vôtre."),
            el("p", "", data.summary.count
              ? "Choisissez une autre note pour continuer votre lecture."
              : this.endpoint ? "Vous avez essayé La Taie du Soir ? Partagez votre expérience." : "Le recueil des avis sera bientôt ouvert."),
          );
          this.grid.append(empty);
        }
        if (this.result) this.result.textContent = `${data.total} avis${this.rating === "0" ? "" : ` · ${this.rating} étoiles`}`;
        this.more.hidden = this.mode === "home" || !data.has_more;
        this.status.textContent = "";
      } catch {
        if (sequence === this.sequence) {
          if (append) this.page--;
          this.status.textContent = "Les avis ne sont pas disponibles pour le moment. ";
          const retry = el("button", "ar-button secondary", "Réessayer");
          retry.type = "button";
          retry.addEventListener("click", () => {
            this.page = 1;
            this.load(false);
          });
          this.status.append(retry);
        }
      } finally {
        if (sequence === this.sequence) {
          this.more.disabled = false;
          this.root.removeAttribute("aria-busy");
        }
      }
    }

    buildForm() {
      this.dialog = el("dialog");
      this.dialog.setAttribute("aria-label", "Partager votre avis");
      const header = el("div", "ar-dialog-head");
      header.append(el("h3", "", "Votre avis compte."));
      const close = el("button", "ar-close", "×");
      close.type = "button";
      close.setAttribute("aria-label", "Fermer le formulaire");
      close.addEventListener("click", () => this.dialog.close());
      header.append(close);
      this.dialog.append(header, el("p", "ar-note", this.demo
        ? "Aperçu du formulaire : aucun envoi n’est effectué."
        : "Votre avis sera publié après modération. Votre e-mail reste privé."));
      const form = el("form", "ar-form");
      const fields = el("div", "ar-fields");
      const field = (label, name, type, min, max, placeholder) => {
        const wrapper = el("label", "", label);
        const input = el(type === "textarea" ? "textarea" : "input");
        if (type !== "textarea") input.type = type;
        else input.rows = 4;
        input.name = name;
        input.required = true;
        input.minLength = min;
        input.maxLength = max;
        input.placeholder = placeholder || "";
        if (name === "email") input.autocomplete = "email";
        if (name === "name") input.autocomplete = "given-name";
        wrapper.append(input);
        return wrapper;
      };
      fields.append(
        field("Prénom ou pseudonyme public", "name", "text", 2, 50, "Camille"),
        field("E-mail privé", "email", "email", 3, 254, "vous@exemple.fr"),
      );
      form.append(fields);
      const rating = el("fieldset");
      rating.append(el("legend", "", "Votre note"));
      const choices = el("div", "ar-rating");
      for (let value = 1; value <= 5; value++) {
        const label = el("label");
        const input = el("input");
        input.type = "radio";
        input.name = "rating";
        input.value = value;
        input.required = true;
        input.setAttribute("aria-label", `${value} sur 5`);
        label.append(input, el("span", "", `${value} ★`));
        choices.append(label);
      }
      rating.append(choices);
      form.append(
        rating,
        field("Un titre pour votre avis", "title", "text", 3, 90, "L’essentiel de votre expérience"),
        field("Votre expérience", "body", "textarea", 20, 2000, "Ce qui vous a plu, moins plu, ou ce que vous aimeriez savoir avant de choisir."),
      );
      const hp = el("label", "ar-hp", "Laissez ce champ vide");
      const trap = el("input");
      trap.name = "website";
      trap.tabIndex = -1;
      trap.autocomplete = "off";
      hp.append(trap);
      form.append(hp);
      const consent = el("label", "ar-check");
      const check = el("input");
      check.type = "checkbox";
      check.name = "consent";
      check.required = true;
      consent.append(check, el("span", "", "J’accepte que mon avis et mon prénom ou pseudonyme soient publiés. Mon e-mail sert uniquement au suivi de cet avis."));
      form.append(consent);
      const privacy = el("a", "ar-note", "Consulter la politique de confidentialité");
      privacy.href = "https://aubeetelle.fr/pages/confidentialite";
      privacy.target = "_blank";
      privacy.rel = "noopener";
      form.append(privacy);
      const output = el("p", "ar-form-status");
      output.setAttribute("role", "status");
      const submit = el("button", "ar-button", "Envoyer mon avis");
      submit.type = "submit";
      form.append(submit, output);
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (this.demo) {
          output.textContent = "Aperçu uniquement : aucun avis n’a été envoyé ni enregistré.";
          return;
        }
        if (!this.endpoint) {
          output.textContent = "Le formulaire n’est pas encore ouvert.";
          return;
        }
        submit.disabled = true;
        output.textContent = "Envoi en cours…";
        const data = new FormData(form);
        try {
          const response = await fetch(this.endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "omit",
            signal: AbortSignal.timeout(12000),
            body: JSON.stringify({
              product: this.dataset.product || "la-taie-du-soir",
              name: data.get("name"), email: data.get("email"),
              rating: Number(data.get("rating")), title: data.get("title"),
              body: data.get("body"), website: data.get("website"),
              consent: data.get("consent") === "on",
            }),
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.error || "L’envoi a échoué.");
          output.textContent = result.message;
          form.reset();
        } catch (error) {
          output.textContent = error.name === "TimeoutError"
            ? "La connexion a pris trop de temps. Vérifiez avant de réessayer."
            : error.message === "Failed to fetch"
              ? "La connexion a échoué. Réessayez dans un instant."
              : error.message;
        } finally {
          submit.disabled = false;
        }
      });
      this.dialog.append(form);
      this.root.append(this.dialog);
    }
  }

  customElements.define("aube-reviews", AubeReviews);
})();
